import { fromJS, Set } from 'immutable';
import { mergeEntity, normalizeEntities } from 'helpers/utilHelpers';

import {
  CONNECT_COMPANY_DRIVE_RESULT,
  CREATE_USER_CLIENT_SUCCESS,
  DELETE_DEPARTMENT_SUCCESS,
  DELETE_FREELANCER_SUCCESS,
  DELETE_POSITION_SUCCESS,
  DELETE_USER_ROLE_SUCCESS,
  DELETE_USER_SUCCESS,
  DISMISS_USER_SUCCESS,
  INVITE_USER_CLIENT_SUCCESS,
  INVITE_USER_SUCCESS,
  LOAD_CLIENT_BY_ID_SUCCESS,
  LOAD_CLIENT_PROFILES_SUCCESS,
  LOAD_CLIENT_USER_BY_ID_SUCCESS,
  LOAD_CLIENTS_SUCCESS,
  LOAD_CMS_LIST_SUCCESS,
  LOAD_COMPANIES_SUCCESS,
  LOAD_DEPARTMENTS_SUCCESS,
  LOAD_DISMISS_USER_SUCCESS,
  LOAD_FREELANCERS_SUCCESS,
  LOAD_POSITIONS_SUCCESS,
  LOAD_PROJECT_BY_ID_SUCCESS,
  LOAD_PROJECTS_SUCCESS,
  LOAD_STRUCTURE_CHART_SUCCESS,
  LOAD_TASK_BY_ID_SUCCESS,
  LOAD_TASKS_SUCCESS,
  LOAD_DRAWER_TASKS_SUCCESS,
  LOAD_USER_BY_ID_SUCCESS,
  LOAD_USER_LAST_TASK_SUCCESS,
  LOAD_USER_ROLES_SUCCESS,
  LOAD_USERS_SUCCESS,
  SET_USERS_ONLINE,
  UPDATE_CLIENT_USER_SUCCESS,
  UPDATE_DEPARTMENT_SUCCESS,
  UPDATE_FREELANCER_SUCCESS,
  UPDATE_POSITION_SUCCESS,
  UPDATE_ROLE_ASSIGNMENT_SUCCESS,
  UPDATE_USER_COMPANY_SUCCESS,
  UPDATE_CLIENT_SUCCESS,
  UPDATE_USER_ROLE_SUCCESS,
  UPDATE_USER_SUCCESS,
  DELETE_TASK_STATUS_SUCCESS,
  CHANGE_COMPANY_RATE_SUCCESS,
  UPDATE_LEGAL_ENTITY_SUCCESS,
  DELETE_LEGAL_ENTITY_SUCCESS,
  UNSET_COMPANY_INTEGRATION_SUCCESS,
  UPDATE_COMPANY_MASTER_PASSWORD_SUCCESS,
  DELETE_CLIENT_SUCCESS,
  DISCONNECT_TELEGRAM_SUCCESS,
  LOAD_CLIENTS_TABLE_SUCCESS,
  CLEAR_USERS,
  CLEAR_DEPARTMENTS,
  CLEAR_POSITIONS,
  CLEAR_CLIENT_PROFILES,
  RE_INVITE_USER,
  CREATE_COMPANY_EXPORT_SUCCESS,
  LOAD_COMPANY_BY_ID_SUCCESS,
  DELETE_CLIENT_PROFILE_SUCCESS,
} from 'constants/ActionTypes';

/**
 * @type {Immutable.Map}
 */
const initialState = fromJS({
  users: {},
  clientProfiles: {},
  positions: {},
  departments: {},
  roles: {},
  freelancers: {},
  cmsList: {},
  companies: {},
  online: null,
});

/**
 * Function for prepare user item.
 * @param user
 * @return {*}
 */
const prepareUser = (user) => {
  const excludingUser = ['projects', 'projectServiceMaps', 'tasks'];

  excludingUser.forEach((field) => user[field] && delete user[field]);

  return user;
};

/**
 * Function for prepare freelancer item
 * @param freelancer
 * @return {{cmsList}|*}
 */
const prepareFreelancer = (freelancer) => {
  if (freelancer.cmsList && typeof freelancer.cmsList === 'string') {
    freelancer.cmsList = JSON.parse(freelancer.cmsList);
  }

  return freelancer;
};

const userReducer = (state = initialState, action) => {
  let users = [],
    clientProfiles = [];
  switch (action.type) {
    case LOAD_CMS_LIST_SUCCESS:
      return state.set('cmsList', fromJS(action.list));
    case LOAD_USERS_SUCCESS: {
      const users = action.users.items.map((user) => prepareUser({ ...user }));
      return state.set('users', normalizeEntities(users, 'userId', state.get('users')));
    }
    case CLEAR_USERS: {
      return state.set('users', initialState.get('users'));
    }
    case LOAD_DEPARTMENTS_SUCCESS:
      return state.set('departments', normalizeEntities(action.departments.items, 'departmentId'));
    case CLEAR_DEPARTMENTS: {
      return state.set('departments', initialState.get('departments'));
    }
    case LOAD_POSITIONS_SUCCESS:
      return state.set('positions', normalizeEntities(action.positions.items, 'positionId'));
    case CLEAR_POSITIONS: {
      return state.set('positions', initialState.get('positions'));
    }
    case LOAD_FREELANCERS_SUCCESS: {
      const freelancers = action.freelancers.items.map((freelancer) => prepareFreelancer({ ...freelancer }));
      return state.set('freelancers', normalizeEntities(freelancers, 'contractorId', state.get('freelancers')));
    }
    case LOAD_CLIENT_USER_BY_ID_SUCCESS: {
      const client = prepareUser({ ...action.user });
      const clientPath = ['clientProfiles', client.userId];
      return state.setIn(clientPath, mergeEntity(client, state.getIn(clientPath)));
    }
    case LOAD_USER_BY_ID_SUCCESS: {
      const user = prepareUser({ ...action.user });
      const userPath = ['users', user.userId];
      return state.setIn(userPath, mergeEntity(user, state.getIn(userPath)));
    }
    case RE_INVITE_USER: {
      if (state.getIn(['users', action.userId])) {
        return state.setIn(['users', action.userId, 'status'], 'invited');
      }

      return state;
    }
    case LOAD_CLIENT_BY_ID_SUCCESS: {
      if (action.values.clientProfiles) {
        const clientProfiles = [];
        action.values.clientProfiles.forEach((profile) => clientProfiles.push({ ...profile }));

        return state.set('clientProfiles', normalizeEntities(clientProfiles, 'userId', state.get('clientProfiles')));
      }

      return state;
    }
    case LOAD_CLIENTS_TABLE_SUCCESS: {
      let clientProfiles = [];
      action.clients.items.forEach((client) => {
        clientProfiles = clientProfiles.concat(client.clientProfiles);
      });
      return state.set('clientProfiles', normalizeEntities(clientProfiles, 'userId', state.get('clientProfiles')));
    }
    case LOAD_USER_ROLES_SUCCESS:
      return state.set('roles', normalizeEntities(action.roles.items, 'roleId', state.get('roles')));
    case UPDATE_USER_ROLE_SUCCESS: {
      const role = { ...action.role };
      return state.setIn(['roles', role.roleId], mergeEntity(role, state.getIn(['roles', role.roleId])));
    }
    case DELETE_USER_ROLE_SUCCESS:
      return state.deleteIn(['roles', action.roleId]);
    case UPDATE_ROLE_ASSIGNMENT_SUCCESS: {
      const { userId, role } = action;
      return state.setIn(['users', parseInt(userId), 'role'], role);
    }
    case UPDATE_DEPARTMENT_SUCCESS: {
      const department = { ...action.department };
      return state.setIn(
        ['departments', department.departmentId],
        mergeEntity(department, state.getIn(['departments', department.departmentId])),
      );
    }
    case DELETE_DEPARTMENT_SUCCESS:
      return state.deleteIn(['departments', action.departmentId]);
    case UPDATE_POSITION_SUCCESS: {
      const position = { ...action.position };
      return state.setIn(
        ['positions', position.positionId],
        mergeEntity(position, state.getIn(['positions', position.positionId])),
      );
    }
    case DELETE_POSITION_SUCCESS:
      return state.deleteIn(['positions', action.positionId]);
    case UPDATE_FREELANCER_SUCCESS: {
      const freelancer = prepareFreelancer({ ...action.freelancer });
      return state.setIn(
        ['freelancers', freelancer.contractorId],
        mergeEntity(freelancer, state.getIn(['freelancers', freelancer.contractorId])),
      );
    }
    case DELETE_FREELANCER_SUCCESS:
      return state.deleteIn(['freelancers', action.freelancerId]);
    case UPDATE_CLIENT_USER_SUCCESS: {
      const profile = { ...action.user };
      return state.setIn(
        ['clientProfiles', action.user.userId],
        mergeEntity(profile, state.getIn(['clientProfiles', action.user.userId])),
      );
    }
    case UPDATE_USER_SUCCESS: {
      const user = action.user.userProfile
        ? prepareUser({ ...action.user.userProfile })
        : prepareUser({ ...action.user });
      return state.setIn(['users', user.userId], mergeEntity(user, state.getIn(['users', user.userId])));
    }
    case CREATE_USER_CLIENT_SUCCESS:
      return state.setIn(['clientProfiles', action.user.user.userId], fromJS(action.user.userProfile));
    case INVITE_USER_SUCCESS: {
      state = state.setIn(['users', action.values.userId], fromJS(action.values));
      let company = state.getIn(['companies', action.values.companyId]);
      if (company?.get('usersCountDetail')) {
        company = company.set('usersCount', parseInt(company.get('usersCount')) + 1);
        company = company.setIn(
          ['usersCountDetail', 'invite'],
          parseInt(company.getIn(['usersCountDetail', 'invite'])) + 1,
        );
        return state.setIn(['companies', action.values.companyId], company);
      }

      return state;
    }
    case DELETE_CLIENT_PROFILE_SUCCESS:
      return state.deleteIn(['clientProfiles', action.userId]);
    case DISMISS_USER_SUCCESS: {
      state = state.setIn(['users', action.user.userId], fromJS(action.user));
      const user = state.getIn(['users', action.user.userId]);
      let company = state.getIn(['companies', user?.get('companyId')]);
      if (user?.get('companyId') && company?.get('usersCountDetail')) {
        company = company.set('usersCount', parseInt(company.get('usersCount')) - 1);
        company = company.setIn(
          ['usersCountDetail', 'active'],
          parseInt(company.getIn(['usersCountDetail', 'active'])) - 1,
        );
        return state.setIn(['companies', user.get('companyId')], company);
      }

      return state;
    }
    case DELETE_CLIENT_SUCCESS: {
      let newState = state;
      if (action.company.clientProfiles?.size) {
        action.company.clientProfiles.forEach((profile) => {
          newState = state.deleteIn(['clientProfiles', profile?.get('userId')]);
        });
      }
      return newState;
    }
    case LOAD_COMPANIES_SUCCESS: {
      let companies = action.companies.items;
      companies.forEach((company) => {
        company.legalEntities = company.legalEntities.map((entity) => entity.entityId);
      });
      return state.set('companies', normalizeEntities(action.companies.items, 'companyId', state.get('companies')));
    }
    case UPDATE_CLIENT_SUCCESS: {
      if (!action.client.name) {
        return state;
      }

      const profiles = state.get('clientProfiles').filter((user) => user.get('clientId') === action.client.companyId);
      return state.set(
        'clientProfiles',
        profiles.map((user) => user.set('companyName', action.client.name)),
      );
    }
    case UPDATE_USER_COMPANY_SUCCESS: {
      let company = action.company;
      company.legalEntities = company.legalEntities.map((entity) => entity.entityId);

      const newState = state.setIn(['companies', company.companyId], fromJS(company));
      return company.name
        ? newState.set(
          'users',
          newState.get('users').map((user) => user.set('companyName', company.name)),
        )
        : newState;
    }
    case DELETE_USER_SUCCESS: {
      const user = state.getIn(['users', action.userId]);
      state = state.deleteIn(['users', action.userId]);

      let company = state.getIn(['companies', user?.get('companyId')]);

      if (user?.get('companyId') && company?.get('usersCountDetail') && user.get('status') !== 'dismissed') {
        const type = user.get('status') === 'active' ? 'active' : 'invite';
        company = company.set('usersCount', parseInt(company.get('usersCount')) - 1);
        company = company.setIn(['usersCountDetail', type], parseInt(company.getIn(['usersCountDetail', type])) - 1);
        return state.setIn(['companies', user.get('companyId')], company);
      }

      return state;
    }
    case LOAD_CLIENT_PROFILES_SUCCESS:
      return state.set('clientProfiles', normalizeEntities(action.users.items, 'userId'));
    case CLEAR_CLIENT_PROFILES: {
      return state.set('clientProfiles', initialState.get('clientProfiles'));
    }
    case LOAD_CLIENTS_SUCCESS: {
      const profiles = [];

      action.clients.items.forEach((client) => {
        client.clientProfiles &&
        client.clientProfiles.forEach((profile) => {
          profiles.push(profile);
        });
      });

      return state.set('clientProfiles', normalizeEntities(profiles, 'userId', state.get('clientProfiles')));
    }
    case LOAD_PROJECTS_SUCCESS: {
      const profiles = [];

      action.projects.items.forEach((project) => {
        if (project.clientProfiles) {
          project.clientProfiles.forEach((profile) => {
            profiles.push(profile);
          });
        }
      });

      return state.set('clientProfiles', normalizeEntities(profiles, 'userId', state.get('clientProfiles')));
    }
    case LOAD_PROJECT_BY_ID_SUCCESS: {
      if (action.project.clientProfiles) {
        const profiles = [];
        action.project.clientProfiles.forEach((profile) => {
          profiles.push(profile);
        });
        return state.set('clientProfiles', normalizeEntities(profiles, 'userId', state.get('clientProfiles')));
      }

      return state;
    }
    case LOAD_STRUCTURE_CHART_SUCCESS: {
      return state.set('structureChart', fromJS(action.chart));
    }
    case SET_USERS_ONLINE: {
      const currentOnline = state.get('online');
      const onlineUsers = action.online && action.online.map((id) => parseInt(id));
      let hasNew = currentOnline?.size !== onlineUsers.length;

      onlineUsers.forEach((user) => {
        if (!currentOnline?.has(user)) {
          hasNew = true;
        }
      });

      if (!hasNew) {
        return state;
      }

      return state.set('online', new Set(onlineUsers));
    }
    case LOAD_USER_LAST_TASK_SUCCESS:
      return state.set('lastTask', fromJS(action.stats.timers));
    case INVITE_USER_CLIENT_SUCCESS:
      if (action.values) {
        return state.setIn(['clientProfiles', action.values.userId], fromJS(action.values));
      }
      return state;
    case LOAD_TASKS_SUCCESS:
    case LOAD_DRAWER_TASKS_SUCCESS: {
      action.tasks.items.forEach((task) => {
        ['responsible', 'creator', 'coPerformer'].forEach((field) => {
          if (task[field]) {
            if (task[field].userType === 'client') {
              clientProfiles.push(task[field]);
            } else {
              users.push(task[field]);
            }
          }
        });
      });

      return state
        .set('users', normalizeEntities(users, 'userId', state.get('users')))
        .set('clientProfiles', normalizeEntities(clientProfiles, 'userId', state.get('clientProfiles')));
    }
    case LOAD_TASK_BY_ID_SUCCESS:
      ['responsible', 'creator', 'coPerformer'].forEach((field) => {
        if (action.task && action.task[field]) {
          if (action.task[field].userType === 'client') {
            clientProfiles.push(action.task[field]);
          } else {
            users.push(action.task[field]);
          }
        }
      });

      return state
        .set('users', normalizeEntities(users, 'userId', state.get('users')))
        .set('clientProfiles', normalizeEntities(clientProfiles, 'userId', state.get('clientProfiles')));
    case LOAD_DISMISS_USER_SUCCESS: {
      if (action.user.managedProjectsIds) {
        return state.setIn(['users', action.user.userId, 'managedProjectsIds'], fromJS(action.user.managedProjectsIds));
      }
      return state;
    }
    case DELETE_TASK_STATUS_SUCCESS: {
      let company = state.getIn(['companies', action.companyId]);

      if (!company) {
        return state;
      }

      return state.setIn(['companies', action.companyId], company);
    }
    case CONNECT_COMPANY_DRIVE_RESULT: {
      if (action.payload.companyId && action.payload.status === 204) {
        return state.setIn(
          ['companies', parseInt(action.payload.companyId), 'googleDrive'],
          fromJS({ status: action.payload.data }),
        );
      }
      return state;
    }
    case CHANGE_COMPANY_RATE_SUCCESS: {
      const companyId = state.get('companies')?.toList()?.get(0)?.get('companyId');
      return state
        .setIn(['companies', companyId, 'companyRate'], fromJS({
          isDefault: action.entities.isDefault,
          isVisible: action.entities.isVisible,
          name: action.entities.name,
          parentId: action.entities.parentId,
          price: action.entities.price,
          projectsLimit: action.entities.projectsLimit,
          rateId: action.entities.rateId,
          status: action.entities.status,
          storageLimit: action.entities.storageLimit,
          usersLimit: action.entities.usersLimit,
        }))
        .setIn(['companies', companyId, 'balance'], action.entities.balance)
        .setIn(['companies', companyId, 'rateId'], action.entities.rateId)
        .setIn(['companies', companyId, 'ratePaymentDate'], null);
    }
    case UPDATE_LEGAL_ENTITY_SUCCESS: {
      if (
        action.entity?.relatedEntity === 'company' &&
        !state.getIn(['companies', action.entity.relatedEntityId, 'legalEntities'])?.includes(action.entity.entityId)
      ) {
        return state.setIn(
          ['companies', action.entity.relatedEntityId, 'legalEntities'],
          state.getIn(['companies', action.entity.relatedEntityId, 'legalEntities']).push(action.entity.entityId),
        );
      }
      return state;
    }
    case DELETE_LEGAL_ENTITY_SUCCESS: {
      let newState = state;
      if (action.companyId && newState.getIn(['companies', action.companyId, 'legalEntities'])?.size) {
        newState = newState.setIn(
          ['companies', action.companyId, 'legalEntities'],
          newState
            .getIn(['companies', action.companyId, 'legalEntities'])
            .filter((entityId) => entityId !== action.entityId),
        );
      }
      return newState;
    }
    case UNSET_COMPANY_INTEGRATION_SUCCESS:
      if (state.getIn(['companies', action.companyId]) && action.integration === 'googleDrive') {
        return state.setIn(['companies', action.companyId, 'googleDrive'], fromJS({ status: action.data }));
      }

      return state;
    case UPDATE_COMPANY_MASTER_PASSWORD_SUCCESS:
      return action?.values?.companyId
        ? state.setIn(
          ['companies', action.values.companyId],
          state.getIn(['companies', action.values.companyId]).merge(fromJS(action.response)),
        )
        : state;
    case DISCONNECT_TELEGRAM_SUCCESS: {
      if (state.getIn(['users', action.userId])) {
        return state.setIn(['users', action.userId, 'isTelegramConnected'], false);
      }
      return state.setIn(['clientProfiles', action.userId, 'isTelegramConnected'], false);
    }
    case CREATE_COMPANY_EXPORT_SUCCESS: {
      return state.setIn(['companies', action.companyExport.companyId, 'companyExport'], fromJS(action.companyExport));
    }
    case LOAD_COMPANY_BY_ID_SUCCESS: {
      const path = ['companies', action.values.companyId];
      return state.setIn(path, state.getIn(path).merge(fromJS(action.values)));
    }
    default:
      return state;
  }
};

export default userReducer;
